import { Component, computed, forwardRef, input, effect, signal, output, model, viewChild, inject, DestroyRef } from "@angular/core";
import { NgModel, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { Observable } from "rxjs";
import { FinancialVendor } from "../../../types/financialvendor";
import { PCPTradePartnerSelectorBaseComponent } from '../pcp-tradepartner-selector.basecomponent';

import { ErrormessageComponent } from "../../validation/errormessage/errormessage.component";
import { AutoCompleteModule } from "primeng/autocomplete";
import { CheckboxModule } from "primeng/checkbox";
import { FluidModule } from "primeng/fluid";
import { ButtonModule } from "primeng/button";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: "pcp-tradepartner-autocomplete",
    templateUrl: "./pcp-tradepartner-autocomplete.component.html",
    styleUrls: ["./pcp-tradepartner-autocomplete.component.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => PCPTradePartnerAutoCompleteComponent),
        }
    ],
    imports: [AutoCompleteModule, FormsModule, FluidModule, ErrormessageComponent, CheckboxModule, ButtonModule]
})
export class PCPTradePartnerAutoCompleteComponent extends PCPTradePartnerSelectorBaseComponent {

    private destroyRef = inject(DestroyRef);

    readonly tradePartnerSelection = viewChild<NgModel>("tradePartnerSelection");

    placeholder = input<string>("Trade Partners");
    forceSelection = input<boolean>(true);
    appendTo = input<string>('self');
    showBusinessName = input<boolean>(false);
    tradePartnersObservable = input<Observable<FinancialVendor[]>>();

    tradePartners = model<FinancialVendor[]>([]);

    readonly onInputClear = output<void>();
    readonly focused = output<void>();

    readonly allTradePartners = signal<FinancialVendor[]>([]);
    readonly selectedTradePartner = signal<FinancialVendor>(null);
    readonly filterValue = signal<string>("", { equal: (a, b) => a === b && b !== '' });
    readonly sortOrder = signal<"name" | "dba">("name");

    private nameSort = (a: FinancialVendor, b: FinancialVendor) => a.name.localeCompare(b.name);
    private businessAsNameSort = (a: FinancialVendor, b: FinancialVendor) => (a.businessAsName ?? a.name).localeCompare(b.businessAsName ?? b.name);

    sortFunction = computed(() => {
        return this.sortOrder() === "dba" ? this.businessAsNameSort : this.nameSort;
    });

    buttonLabel = computed(() => {
        return this.sortOrder() === "dba" ? "Business Name" : "Financial Name";
    });

    tradePartnerSuggestions = computed(() => {
        const filter = this.filterValue();
        const tradePartners = this.tradePartners();
        const sortFunc = this.sortFunction();

        if (!filter) {
            const sortedTradePartners = [...tradePartners].sort(sortFunc);
            return sortedTradePartners;
        }

        const suggestions = [...tradePartners].filter((tradePartner) => tradePartner.name.toLowerCase().includes(filter.toLowerCase())).sort(sortFunc);
        return suggestions;
    });

    loadTradePartners = effect(() => {
        const tradePartners$ = this.tradePartnersObservable();
        if (tradePartners$) {
            this.reloadOptions();
        }
    });

    get label(): string {
        if (this.isLoading) return this.loadingText;

        return this.placeholder();
    }

    writeValue(obj: any) {
        this.selectedTradePartner.set(obj);
    }

    toggleSortOrder() {
        this.sortOrder.update(sortOrder => sortOrder === "name" ? "dba" : "name");
    }

    onComplete(event: any) {
        this.filterValue.set(event && event.query || "")
    }

    clearTradePartner() {
        this.selectedTradePartner.set(null);
        this.propagateChange(null);
        this.propagateModelTouched();
        this.onInputClear.emit();
    }

    reloadOptions(): void {
        if (!this.tradePartnersObservable()) { return; }

        this.isLoading = true;
        this.tradePartnersObservable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((vendors: FinancialVendor[]) => {
                this.tradePartners.set(vendors);
                this.allTradePartners.set(vendors);
                this.isLoading = false;
                setTimeout(() => {
                    this.tradePartnersLoaded.emit();
                })
            });
    }

    selectTradePartner(vendor: FinancialVendor, addIfNotPresent: boolean = false) {
        if (!vendor) { return; }

        const tradePartnerIsNotPresent = !this.tradePartners().some((tradePartner) => vendor.id === tradePartner.id);
        if (tradePartnerIsNotPresent && addIfNotPresent) {
            this.tradePartners.update(tradePartners => [vendor, ...tradePartners]);
        }

        this.selectedTradePartner.set(this.tradePartners().find((tradePartner) => vendor.id === tradePartner.id));
    }

    filterOptionsByVendorIds(vendorIds: number[]) {
        this.tradePartners.set(this.allTradePartners().filter((tradePartner) => vendorIds.includes(tradePartner.id)));
    }

    showAllOptions() {
        this.tradePartners.set(this.allTradePartners());
    }
}
